import React, { useContext, useMemo } from 'react';
import { Grid, Skeleton } from '@mui/material';
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import { useSurgeryData } from '../../hooks/useSurgeryData';
import { surgeryChartsForMode, ChartConfig } from '../../config/analyticsCharts';
import ErrorState from '../../common/ErrorState';
import ChartFactory from '../../charts/ChartFactory';
import SurgeryKpiSection from '../../charts/SurgeryKpiSection';
import { IDepartment } from '../../../../../constants/types';
import { AnalyticsViewMode, EMPTY_SURGERY_ANALYTICS_FILTERS, SourceState, SurgeryAnalyticsFiltersQuery } from '../../types';

interface SurgeryTabProps {
  investigation: {
    permissions: string[];
    uuid: string;
    [key: string]: any;
  };
  departmentSelected: string;
  startDate: number | null;
  endDate: number | null;
  departments: IDepartment[];
  mode: AnalyticsViewMode;
  surgeryFilters: SurgeryAnalyticsFiltersQuery;
}

const SurgeryTab: React.FC<SurgeryTabProps> = ({
  investigation,
  departmentSelected,
  startDate,
  endDate,
  departments,
  mode,
  surgeryFilters,
}) => {
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);

  const periodLabel = useMemo(() => {
    if (!startDate || !endDate) {
      return '';
    }
    return translate('hospital.analytics.surgery.activity.period_label') as string;
  }, [startDate, endDate, translate]);

  const { data, sourceStates } = useSurgeryData({
    investigation,
    departmentSelected,
    startDate: startDate?.toString() || '',
    endDate: endDate?.toString() || '',
    mode,
    filters: surgeryFilters ?? EMPTY_SURGERY_ANALYTICS_FILTERS,
    periodLabel,
  });

  const chartsForMode = useMemo(() => surgeryChartsForMode(mode), [mode]);
  const visibleCharts = useMemo(
    () => chartsForMode.filter((chart) => chart.visible && chart.type !== 'surgery-kpi-section'),
    [chartsForMode],
  );

  const defaultDepartment = useMemo(
    () => (departments?.length ? departments[0] : null),
    [departments],
  );

  const localizePeriodActivity = (chartData: any) => {
    if (!chartData?.series) {
      return chartData;
    }
    const labelMap: Record<string, string> = {
      planned: 'hospital.analytics.surgery.kpis.planned',
      completed: 'hospital.analytics.surgery.kpis.completed',
      cancelled: 'hospital.analytics.surgery.kpis.cancelled',
    };
    return {
      ...chartData,
      series: chartData.series.map((series: { name: string; data: number[] }) => ({
        ...series,
        name: labelMap[series.name]
          ? (translate(labelMap[series.name]) as string)
          : series.name,
      })),
    };
  };

  const renderCard = (chart: ChartConfig, state: SourceState | undefined) => {
    switch (state?.status) {
      case 'pending':
        return <Skeleton variant="rectangular" width="100%" height={300} />;
      case 'error':
        return <ErrorState error={state.error} />;
      default: {
        let chartData = (data as any)[chart.source];
        if (chart.source === 'periodActivity') {
          chartData = localizePeriodActivity(chartData);
        }
        return (
          <ChartFactory
            id={chart.id}
            type={chart.type}
            title={chart.title}
            data={chartData}
            departments={departments}
            departmentSelected={defaultDepartment}
            topN={chart.topN}
          />
        );
      }
    }
  };

  const kpiState = sourceStates.quickKpis;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <SurgeryKpiSection
          totals={(data as any).quickKpis}
          loading={kpiState?.status === 'pending'}
        />
        {kpiState?.status === 'error' && <ErrorState error={kpiState.error} />}
      </Grid>
      {visibleCharts.map((chart) => (
        <Grid
          item
          xs={12}
          sm={chart.cols === 12 ? 12 : 6}
          md={chart.cols}
          key={chart.id}
        >
          {renderCard(chart, sourceStates[chart.source])}
        </Grid>
      ))}
    </Grid>
  );
};

export default SurgeryTab;
